// React half of the drawing interaction layer.
//
// The state layer (types, reducer, helpers) moved to ./drawingInteractionState during
// Phase 2 of PLAN_DRAWING_SSOT_MIGRATION_2026-09-27 so engine-drawing can own it
// without pulling React in. The pure names are re-exported below for compatibility.
import { createDrawingInteractionState, drawingInteractionReducer, getSelectedObjectIds, offsetDrawingByPixels, replaceDrawingById, } from "@vninvestcharts/engine-drawing";
import { useCallback, useMemo, useReducer } from "react";
import { deleteDrawingGroup, resolveDrawingGroupMemberIds } from "@vninvestcharts/engine-core";
export function useDrawingInteraction(initialDrawings = []) {
    const [state, dispatch] = useReducer(drawingInteractionReducer, initialDrawings, createDrawingInteractionState);
    const selectObject = useCallback((objectId) => {
        dispatch({ type: "SELECT_OBJECT", objectId });
    }, [dispatch]);
    const setSelectedObjects = useCallback((objectIds) => {
        dispatch({ type: "SET_SELECTED_OBJECTS", objectIds: [...objectIds] });
    }, [dispatch]);
    const startMoving = useCallback((objectId, startPoint, currentPoint) => {
        dispatch({ type: "START_MOVING", objectId, startPoint, currentPoint });
    }, [dispatch]);
    const startResizing = useCallback((objectId, handle) => {
        dispatch({ type: "START_RESIZING", objectId, handle });
    }, [dispatch]);
    const startEditing = useCallback((objectId, text) => {
        dispatch({ type: "START_EDITING", objectId, text });
    }, [dispatch]);
    const replaceDrawings = useCallback((drawings) => {
        dispatch({ type: "REPLACE", drawings: [...drawings] });
    }, [dispatch]);
    const reconcileDrawings = useCallback((drawings) => {
        dispatch({ type: "RECONCILE", drawings: [...drawings] });
    }, [dispatch]);
    const resetScope = useCallback((drawings) => {
        dispatch({ type: "RESET_SCOPE", drawings: [...drawings] });
    }, [dispatch]);
    const updateDrawing = useCallback((objectId, patch) => {
        const nextDrawings = replaceDrawingById(state.history.present, objectId, patch);
        if (!nextDrawings) {
            return;
        }
        dispatch({ type: "REPLACE", drawings: nextDrawings });
    }, [dispatch, state.history.present]);
    const undo = useCallback(() => {
        dispatch({ type: "UNDO" });
    }, [dispatch]);
    const redo = useCallback(() => {
        dispatch({ type: "REDO" });
    }, [dispatch]);
    const cancelDrawing = useCallback(() => {
        dispatch({ type: "CANCEL" });
    }, [dispatch]);
    const deleteSelected = useCallback(() => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0) {
            return;
        }
        const selectedDrawings = state.history.present.filter((drawing) => selectedObjectIds.includes(drawing.id));
        const unlocked = selectedDrawings.filter((d) => !d.locked);
        const locked = selectedDrawings.filter((d) => d.locked);
        // If all selected drawings are locked, nothing to delete
        if (unlocked.length === 0) {
            return;
        }
        // Delete only unlocked drawings
        const unlockedIds = new Set(unlocked.map((d) => d.id));
        const nextDrawings = state.history.present.filter((drawing) => !unlockedIds.has(drawing.id));
        if (nextDrawings.length !== state.history.present.length) {
            dispatch({ type: "REPLACE", drawings: nextDrawings });
        }
        // Re-select only locked drawings (if any)
        if (locked.length > 0) {
            dispatch({ type: "SET_SELECTED_OBJECTS", objectIds: locked.map((d) => d.id) });
        }
        else {
            dispatch({ type: "CANCEL" });
        }
    }, [dispatch, state.drawingState, state.history.present]);
    const selectGroup = useCallback((groupId) => {
        dispatch({ type: "SET_SELECTED_OBJECTS", objectIds: resolveDrawingGroupMemberIds(state.history.present, groupId) });
    }, [dispatch, state.history.present]);
    const deleteGroup = useCallback((groupId) => {
        const nextDrawings = deleteDrawingGroup(state.history.present, groupId);
        if (nextDrawings.length !== state.history.present.length) {
            dispatch({ type: "REPLACE", drawings: nextDrawings });
        }
        dispatch({ type: "CANCEL" });
    }, [dispatch, state.history.present]);
    const bringSelectedForward = useCallback(() => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0)
            return;
        const nextDrawings = state.history.present.map((drawing) => {
            if (selectedObjectIds.includes(drawing.id)) {
                const currentZ = drawing.zIndex ?? 0;
                const nextZ = state.history.present
                    .filter((d) => (d.zIndex ?? 0) > currentZ)
                    .reduce((min, d) => Math.min(min, d.zIndex ?? 0), Infinity);
                return {
                    ...drawing,
                    zIndex: nextZ !== Infinity ? nextZ : currentZ + 1,
                    updatedAt: Date.now(),
                };
            }
            return drawing;
        });
        dispatch({ type: "REPLACE", drawings: nextDrawings });
    }, [dispatch, state.drawingState, state.history.present]);
    const sendSelectedBackward = useCallback(() => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0)
            return;
        const nextDrawings = state.history.present.map((drawing) => {
            if (selectedObjectIds.includes(drawing.id)) {
                const currentZ = drawing.zIndex ?? 0;
                const prevZ = state.history.present
                    .filter((d) => (d.zIndex ?? 0) < currentZ)
                    .reduce((max, d) => Math.max(max, d.zIndex ?? 0), -Infinity);
                return {
                    ...drawing,
                    zIndex: prevZ !== -Infinity ? prevZ : currentZ - 1,
                    updatedAt: Date.now(),
                };
            }
            return drawing;
        });
        dispatch({ type: "REPLACE", drawings: nextDrawings });
    }, [dispatch, state.drawingState, state.history.present]);
    const duplicateSelected = useCallback(() => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0)
            return;
        const nextDrawings = [...state.history.present];
        const duplicatedIds = [];
        selectedObjectIds.forEach((id) => {
            const drawing = state.history.present.find((d) => d.id === id);
            if (drawing) {
                const nextClone = offsetDrawingByPixels(drawing, 10, 10, undefined, undefined, undefined, 60000, {
                    paneId: drawing.paneId,
                    yScaleId: drawing.yScaleId,
                });
                nextDrawings.push(nextClone);
                duplicatedIds.push(nextClone.id);
            }
        });
        if (duplicatedIds.length > 0) {
            dispatch({ type: "REPLACE", drawings: nextDrawings });
            if (duplicatedIds.length === 1) {
                dispatch({ type: "SELECT_OBJECT", objectId: duplicatedIds[0] });
            }
            else {
                dispatch({ type: "SET_SELECTED_OBJECTS", objectIds: duplicatedIds });
            }
        }
    }, [dispatch, state.drawingState, state.history.present]);
    const toggleSelectedLock = useCallback((locked) => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0)
            return;
        const nextDrawings = state.history.present.map((drawing) => {
            if (selectedObjectIds.includes(drawing.id)) {
                const nextLocked = locked !== undefined ? locked : !drawing.locked;
                return { ...drawing, locked: nextLocked, updatedAt: Date.now() };
            }
            return drawing;
        });
        dispatch({ type: "REPLACE", drawings: nextDrawings });
    }, [dispatch, state.drawingState, state.history.present]);
    const toggleSelectedVisible = useCallback((visible) => {
        const selectedObjectIds = getSelectedObjectIds(state.drawingState);
        if (selectedObjectIds.length === 0)
            return;
        const nextDrawings = state.history.present.map((drawing) => {
            if (selectedObjectIds.includes(drawing.id)) {
                const nextVisible = visible !== undefined ? visible : drawing.visible === false;
                return { ...drawing, visible: nextVisible, updatedAt: Date.now() };
            }
            return drawing;
        });
        dispatch({ type: "REPLACE", drawings: nextDrawings });
    }, [dispatch, state.drawingState, state.history.present]);
    return useMemo(() => ({
        drawingState: state.drawingState,
        history: state.history,
        dispatch,
        selectObject,
        setSelectedObjects,
        startMoving,
        startResizing,
        startEditing,
        replaceDrawings,
        reconcileDrawings,
        resetScope,
        updateDrawing,
        undo,
        redo,
        deleteSelected,
        cancelDrawing,
        selectGroup,
        deleteGroup,
        bringSelectedForward,
        sendSelectedBackward,
        duplicateSelected,
        toggleSelectedLock,
        toggleSelectedVisible,
        canUndo: state.history.past.length > 0,
        canRedo: state.history.future.length > 0,
        allDrawings: state.history.present,
    }), [cancelDrawing, deleteGroup, deleteSelected, dispatch, redo, replaceDrawings, reconcileDrawings, resetScope, selectGroup, selectObject, setSelectedObjects, startEditing, startMoving, startResizing, state.drawingState, state.history, undo, updateDrawing, bringSelectedForward, sendSelectedBackward, duplicateSelected, toggleSelectedLock, toggleSelectedVisible]);
}
